/** 시설 웨어러블 생체·바이오리듬 참조 기준 (backend DEFAULT_POOL_WEARABLE_BASELINE과 동기) */

export interface BaselineMetricPercentiles {
  p10: number;
  p50: number;
  p90: number;
  unit: string;
}

export interface PoolWearableBaselinePopulationFallback {
  hr_low_threshold: number;
  hr_high_threshold: number;
  spo2_threshold: number;
  motion_threshold: number;
}

export interface PoolWearableBaselinePersonalDeviation {
  hr_high_factor: number;
  hr_low_factor: number;
  spo2_low_factor: number;
  max_boost: number;
}

export type WearableBaselineMetricKey =
  | 'heart_rate'
  | 'spo2'
  | 'body_temperature'
  | 'systolic_pressure'
  | 'diastolic_pressure'
  | 'motion';

export interface PoolWearableBaselineDefaults {
  enabled: boolean;
  min_samples_for_personal: number;
  notes: string;
  metrics: Record<WearableBaselineMetricKey, BaselineMetricPercentiles>;
  population_fallback: PoolWearableBaselinePopulationFallback;
  personal_deviation: PoolWearableBaselinePersonalDeviation;
}

export const WEARABLE_BASELINE_METRIC_LABELS: Record<WearableBaselineMetricKey, string> = {
  heart_rate: '심박수 (HR)',
  spo2: '산소포화도 (SpO₂)',
  body_temperature: '체온',
  systolic_pressure: '수축기 혈압',
  diastolic_pressure: '이완기 혈압',
  motion: '움직임·활동',
};

export const DEFAULT_POOL_WEARABLE_BASELINE: PoolWearableBaselineDefaults = {
  enabled: true,
  min_samples_for_personal: 5,
  notes: '',
  metrics: {
    heart_rate: { p10: 60, p50: 72, p90: 100, unit: 'bpm' },
    spo2: { p10: 95, p50: 98, p90: 100, unit: '%' },
    body_temperature: { p10: 36, p50: 36.5, p90: 37.2, unit: '°C' },
    systolic_pressure: { p10: 100, p50: 120, p90: 140, unit: 'mmHg' },
    diastolic_pressure: { p10: 60, p50: 80, p90: 90, unit: 'mmHg' },
    motion: { p10: 0.2, p50: 1, p90: 5, unit: 'g' },
  },
  population_fallback: {
    hr_low_threshold: 40,
    hr_high_threshold: 160,
    spo2_threshold: 92,
    motion_threshold: 5,
  },
  personal_deviation: {
    hr_high_factor: 1.12,
    hr_low_factor: 0.88,
    spo2_low_factor: 0.98,
    max_boost: 30,
  },
};

const METRIC_KEYS = Object.keys(WEARABLE_BASELINE_METRIC_LABELS) as WearableBaselineMetricKey[];

function num(v: unknown, fallback: number): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export function mergePoolWearableBaselineDefaults(
  partial?: Partial<PoolWearableBaselineDefaults> | null
): PoolWearableBaselineDefaults {
  const d = DEFAULT_POOL_WEARABLE_BASELINE;
  const p = partial || {};
  const metrics = { ...d.metrics };
  const pm = p.metrics || {};
  for (const key of METRIC_KEYS) {
    const base = d.metrics[key];
    const raw = pm[key];
    metrics[key] = {
      p10: num(raw?.p10, base.p10),
      p50: num(raw?.p50, base.p50),
      p90: num(raw?.p90, base.p90),
      unit: raw?.unit != null ? String(raw.unit) : base.unit,
    };
  }
  const pf: Partial<PoolWearableBaselinePopulationFallback> = p.population_fallback ?? {};
  const pd: Partial<PoolWearableBaselinePersonalDeviation> = p.personal_deviation ?? {};
  return {
    enabled: p.enabled !== undefined ? Boolean(p.enabled) : d.enabled,
    min_samples_for_personal: Math.min(30, Math.max(3, num(p.min_samples_for_personal, d.min_samples_for_personal))),
    notes: p.notes != null ? String(p.notes).slice(0, 2000) : d.notes,
    metrics,
    population_fallback: {
      hr_low_threshold: num(pf.hr_low_threshold, d.population_fallback.hr_low_threshold),
      hr_high_threshold: num(pf.hr_high_threshold, d.population_fallback.hr_high_threshold),
      spo2_threshold: num(pf.spo2_threshold, d.population_fallback.spo2_threshold),
      motion_threshold: num(pf.motion_threshold, d.population_fallback.motion_threshold),
    },
    personal_deviation: {
      hr_high_factor: num(pd.hr_high_factor, d.personal_deviation.hr_high_factor),
      hr_low_factor: num(pd.hr_low_factor, d.personal_deviation.hr_low_factor),
      spo2_low_factor: num(pd.spo2_low_factor, d.personal_deviation.spo2_low_factor),
      max_boost: num(pd.max_boost, d.personal_deviation.max_boost),
    },
  };
}

export const POOL_WEARABLE_BASELINE_INTRO =
  '웨어러블이 수집하는 생체 신호의 참조 기준(p10·중앙값·p90). ' +
  '회원 개인 기준선 샘플이 부족할 때 이 시설 값을 기준으로 이상 여부를 판단. ' +
  '회원별 동의·개인 버퍼는 「회원 관리 → 개인 안전 기준선」에서 확인.';
